import * as RSlider from '@radix-ui/react-slider'
import * as RSwitch from '@radix-ui/react-switch'
import * as RTooltip from '@radix-ui/react-tooltip'
import * as RToggleGroup from '@radix-ui/react-toggle-group'
import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'

/* Original primitives built on Radix behaviour, styled from scratch. Radix
   gives us the keyboard handling and ARIA wiring; the look is all ours. */

export function Panel({
  children,
  className,
  active = false,
  title,
  right,
}: {
  children: ReactNode
  className?: string
  active?: boolean
  title?: string
  right?: ReactNode
}) {
  return (
    <section
      className={cn(
        'relative overflow-hidden rounded-lg border border-line bg-surface shadow-panel',
        className,
      )}
    >
      {active && <div className="accent-line" aria-hidden="true" />}
      {title && (
        <header className="flex items-center justify-between gap-3 border-b border-line px-3.5 py-2.5">
          <h2 className="text-2xs font-semibold uppercase tracking-[0.14em] text-faint">{title}</h2>
          {right}
        </header>
      )}
      {children}
    </section>
  )
}

export function Button({
  children,
  onClick,
  variant = 'ghost',
  disabled,
  className,
  type = 'button',
  ariaLabel,
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'ghost' | 'danger'
  disabled?: boolean
  className?: string
  type?: 'button' | 'submit'
  ariaLabel?: string
}) {
  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      whileTap={disabled ? undefined : { scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-40',
        variant === 'primary' &&
          'bg-accent text-[#04120a] hover:brightness-110 disabled:hover:brightness-100',
        variant === 'ghost' &&
          'border border-line bg-surface-2 text-ink hover:border-accent/50 hover:text-accent',
        variant === 'danger' && 'border border-danger/40 bg-transparent text-danger hover:bg-danger/10',
        className,
      )}
    >
      {children}
    </motion.button>
  )
}

export function Tooltip({ label, children }: { label: string; children: ReactNode }) {
  return (
    <RTooltip.Root delayDuration={250}>
      <RTooltip.Trigger asChild>{children}</RTooltip.Trigger>
      <RTooltip.Portal>
        <RTooltip.Content
          sideOffset={6}
          className="z-50 max-w-[16rem] rounded-md border border-line bg-surface-2 px-2.5 py-1.5 text-2xs leading-relaxed text-muted shadow-panel"
        >
          {label}
          <RTooltip.Arrow className="fill-[var(--color-surface-2)]" />
        </RTooltip.Content>
      </RTooltip.Portal>
    </RTooltip.Root>
  )
}

export function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format,
  hint,
}: {
  label: string
  value: number
  min: number
  max: number
  step: number
  onChange: (v: number) => void
  format?: (v: number) => string
  hint?: string
}) {
  const id = `slider-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-xs text-muted">
          {label}
        </label>
        <span className="font-mono text-2xs tabular-nums text-accent">
          {format ? format(value) : value}
        </span>
      </div>
      <RSlider.Root
        id={id}
        className="relative flex h-4 w-full touch-none select-none items-center"
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(v) => onChange(v[0] ?? value)}
        aria-label={label}
      >
        <RSlider.Track className="relative h-[3px] w-full grow rounded-full bg-surface-2">
          <RSlider.Range className="absolute h-full rounded-full bg-accent" />
        </RSlider.Track>
        <RSlider.Thumb
          className="block h-3.5 w-3.5 rounded-full border-2 border-accent bg-surface shadow-glow transition-transform hover:scale-110 focus-visible:scale-110"
          aria-label={label}
        />
      </RSlider.Root>
      {hint && <p className="text-2xs leading-relaxed text-faint">{hint}</p>}
    </div>
  )
}

export function Switch({
  label,
  checked,
  onChange,
  hint,
}: {
  label: string
  checked: boolean
  onChange: (v: boolean) => void
  hint?: string
}) {
  const id = `switch-${label.replace(/\s+/g, '-').toLowerCase()}`
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0">
        <label htmlFor={id} className="text-xs text-muted">
          {label}
        </label>
        {hint && <p className="mt-0.5 text-2xs leading-relaxed text-faint">{hint}</p>}
      </div>
      <RSwitch.Root
        id={id}
        checked={checked}
        onCheckedChange={onChange}
        className={cn(
          'relative h-5 w-9 shrink-0 rounded-full border transition-colors',
          checked ? 'border-accent bg-accent/25' : 'border-line bg-surface-2',
        )}
      >
        <RSwitch.Thumb
          className={cn(
            'block h-3.5 w-3.5 translate-x-[3px] rounded-full transition-transform duration-200',
            checked ? 'translate-x-[19px] bg-accent' : 'bg-faint',
          )}
        />
      </RSwitch.Root>
    </div>
  )
}

export function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string; hint?: string }[]
  label: string
}) {
  return (
    <RToggleGroup.Root
      type="single"
      value={value}
      onValueChange={(v) => v && onChange(v as T)}
      aria-label={label}
      className="grid grid-flow-col rounded-md border border-line bg-surface-2 p-0.5"
    >
      {options.map((opt) => (
        <RToggleGroup.Item
          key={opt.value}
          value={opt.value}
          title={opt.hint}
          className={cn(
            'relative rounded-[5px] px-3 py-1.5 text-xs font-medium transition-colors',
            value === opt.value ? 'text-[#04120a]' : 'text-muted hover:text-ink',
          )}
        >
          {value === opt.value && (
            <motion.span
              layoutId={`seg-${label}`}
              className="absolute inset-0 rounded-[5px] bg-accent"
              transition={{ type: 'spring', stiffness: 480, damping: 36 }}
            />
          )}
          <span className="relative z-10">{opt.label}</span>
        </RToggleGroup.Item>
      ))}
    </RToggleGroup.Root>
  )
}

export function Meter({
  label,
  value,
  max = 1,
  signed = false,
  danger,
}: {
  label: string
  value: number
  max?: number
  signed?: boolean
  danger?: boolean
}) {
  const pct = Math.min(100, (Math.abs(value) / max) * 100)
  return (
    <div className="space-y-1">
      <div className="flex justify-between font-mono text-2xs tabular-nums">
        <span className="text-faint">{label}</span>
        <span className={danger ? 'text-warn' : 'text-muted'}>{value.toFixed(2)}</span>
      </div>
      <div className="relative h-1 overflow-hidden rounded-full bg-surface-2">
        {signed && <div className="absolute left-1/2 top-0 h-full w-px bg-line" />}
        <motion.div
          className={cn('h-full rounded-full', danger ? 'bg-warn' : 'bg-accent')}
          style={
            signed
              ? { marginLeft: value < 0 ? `${50 - pct / 2}%` : '50%', width: `${pct / 2}%` }
              : { width: `${pct}%` }
          }
          transition={{ duration: 0.12 }}
        />
      </div>
    </div>
  )
}

export const TooltipProvider = RTooltip.Provider
